import React, { Fragment, useContext, useMemo, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import { GetColor, GetScheme, LoadingSize, Spinner } from 'venn-ui-kit';
import {
  createProxyMetricsKey,
  FREQUENCY_TO_ENUM,
  type FrequencyTypes,
  getFormattedProxyType,
  isRequestSuccessful,
  Numbers,
  ReturnsCalculations,
  useHasFF,
} from 'venn-utils';
import type { LegendSeries } from '../../legend/Legend';
import { Legend } from '../../legend/Legend';
import type { ProxyMetrics } from 'venn-api';
import { investmentProxyMetrics } from 'venn-api';
import Chart from './ProxyInfoChart';
import InterpolationSnapshot from './InterpolationSnapshot';
import type { FundProxy } from '../pickers/types';
import type { BulkPickerDetailedProxyMetadata } from '../../hooks/useProxyDataQuery';
import { capitalize, compact } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { extractProxiedReturns, formatDateRange } from '../../utils/proxyUtils';
import { LayeredProxyInfoContent, useProxyMetricsData } from './LayeredProxyInfo';

interface ProxyInfoProps {
  proxy: FundProxy | BulkPickerDetailedProxyMetadata;
  investment: {
    frequency: number;
    id: string;
    updated: number;
    unproxiedStartRange: number | undefined;
    unproxiedEndRange: number | undefined;
    startRange: number | undefined;
    endRange: number | undefined;
  };
}

export const ProxyInfo: React.FC<React.PropsWithChildren<ProxyInfoProps>> = ({ investment, proxy }) => {
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');
  return hasLayeredProxyFF ? (
    <LayeredProxyInfoTooltip investment={investment} />
  ) : (
    <LegacyProxyInfoTooltip
      investmentId={investment.id}
      investmentFrequency={investment.frequency}
      investmentLastUpdated={investment.updated}
      proxy={proxy}
    />
  );
};

export default ProxyInfo;

interface LegacyProxyInfoTooltipProps {
  investmentId: string;
  investmentLastUpdated: number;
  proxy: FundProxy | BulkPickerDetailedProxyMetadata;
  investmentFrequency: FrequencyTypes;
}

const LegacyProxyInfoTooltip: React.FC<React.PropsWithChildren<LegacyProxyInfoTooltipProps>> = ({
  investmentId,
  investmentLastUpdated,
  proxy,
  investmentFrequency,
}) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const [error, setError] = useState<string | null>(null);
  const { Schemes } = useContext(ThemeContext);

  const { data: suggestion, status } = useQuery<ProxyMetrics | null>(
    [
      ...createProxyMetricsKey(investmentId),
      investmentLastUpdated,
      proxy.proxyId,
      proxy.proxyType,
      proxy.numLags,
      proxy.extrapolate,
    ],
    async () => {
      try {
        const result = await investmentProxyMetrics(investmentId, proxySetId);
        if (isRequestSuccessful(result)) {
          setError(null);
          return result.content;
        }
        setError('Proxy fetch failed');
        return null;
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e.name !== 'AbortError') {
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          setError(e.content?.message || 'Proxy fetch failed');
        }
        return null;
      }
    },
  );

  const extrapolate = !!(suggestion?.extrapolatedStart && suggestion?.extrapolatedEnd);
  const returns = useMemo(() => {
    const allTogether = suggestion?.cumulativeProxiedReturns
      ? ReturnsCalculations.toDataPoints(suggestion.cumulativeProxiedReturns)
      : [];
    return extractProxiedReturns(
      allTogether,
      suggestion?.proxiedStart,
      suggestion?.proxiedEnd,
      suggestion?.extrapolatedStart,
    );
  }, [suggestion]);
  const proxyReturns = returns.proxy;
  const extrapolatedReturns = returns.extrapolated;
  const unproxiedInvestmentReturns = ReturnsCalculations.toDataPoints(suggestion?.investmentReturns ?? []);

  if (error) {
    return <p>{error}</p>;
  }
  if (status === 'loading' || !suggestion) {
    return <Spinner size={LoadingSize.medium} />;
  }

  const legend: LegendSeries[] = compact([
    proxyReturns.length > 0 ? { color: Schemes.Proxy.proxyLine, name: 'Proxy' } : null,
    unproxiedInvestmentReturns.length > 0 ? { color: Schemes.Proxy.invertedSubjectLine, name: 'Investment' } : null,
    extrapolatedReturns.length > 0 ? { color: Schemes.Proxy.extrapolation, name: 'Extrapolation' } : null,
  ]);

  const { proxyType, numLags } = proxy;
  const name = 'name' in proxy ? proxy.name : 'proxyName' in proxy ? proxy.proxyName : undefined;
  const unproxiedFrequency = 'unproxiedFrequency' in proxy ? proxy.unproxiedFrequency : undefined;
  const proxiedFrequency = 'proxiedFrequency' in proxy ? proxy.proxiedFrequency : undefined;
  return (
    <Container>
      {name && (
        <Stat>
          <Title>Returns Proxy Data:</Title>
          <HighlightedValue>{name}</HighlightedValue>
        </Stat>
      )}
      {proxyType && (
        <Stat>
          <Title>Proxy Type:</Title>
          <HighlightedValue>{getFormattedProxyType(proxyType)}</HighlightedValue>
        </Stat>
      )}
      {!!numLags && (
        <Stat>
          <Title>{unproxiedFrequency ? `${capitalize(unproxiedFrequency)} ` : ''}Lag:</Title>
          <HighlightedValue>{numLags}</HighlightedValue>
        </Stat>
      )}
      {extrapolate && proxyType !== 'EXTRAPOLATE' && (
        <Stat>
          <Title>Extrapolation:</Title>
          <HighlightedValue>On</HighlightedValue>
        </Stat>
      )}
      <Stats>
        <Stat>
          <Title>Proxy Range:</Title>
          {suggestion.proxiedStart && suggestion.proxiedEnd && (
            <HighlightedValue>
              {formatDateRange(
                suggestion.proxiedStart,
                suggestion.proxiedEnd,
                // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
                FREQUENCY_TO_ENUM[proxiedFrequency ?? investmentFrequency],
              )}
            </HighlightedValue>
          )}
          {extrapolate && (
            <HighlightedValue>
              {formatDateRange(
                suggestion.extrapolatedStart!,
                suggestion.extrapolatedEnd!,
                // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
                FREQUENCY_TO_ENUM[proxiedFrequency ?? investmentFrequency],
              )}
            </HighlightedValue>
          )}
        </Stat>
        <Stat>
          <Title>Correlation:</Title>
          <Value className="text-right">{Numbers.formatCorrelation(suggestion.correlation)}</Value>
        </Stat>
      </Stats>
      {/* TODO: unify the two charts below into one chart */}
      {unproxiedInvestmentReturns && proxyReturns && suggestion.proxyType && (
        <>
          <ChartContainer>
            {proxyType === 'INTERPOLATE' ? (
              <InterpolationSnapshot
                primarySeries={unproxiedInvestmentReturns}
                overlaySeries={proxyReturns}
                extrapolateSeries={extrapolatedReturns}
                height={54}
                invert
              />
            ) : (
              <Chart
                className="w-full"
                investment={unproxiedInvestmentReturns}
                proxy={proxyReturns ?? []}
                extrapolation={extrapolatedReturns ?? []}
                proxiesShortInfo={[
                  {
                    proxyType: suggestion.proxyType,
                    proxiedStart: suggestion.proxiedStart,
                    proxiedEnd: suggestion.proxiedEnd,
                  },
                ]}
              />
            )}
          </ChartContainer>
          <LegendContainer>
            <Legend className="proxy-info-modal-legend" series={legend} />
          </LegendContainer>
        </>
      )}
    </Container>
  );
};

type LayeredProxyInfoTooltipProps = Pick<ProxyInfoProps, 'investment'>;

const LayeredProxyInfoTooltip: React.FC<LayeredProxyInfoTooltipProps> = ({ investment }) => {
  const investmentId = investment.id;
  const investmentLastUpdated = investment.updated;
  const proxySetId = useRecoilValue(proxySetIdState);
  const { Schemes } = useContext(ThemeContext);

  const { layeredProxyMetrics, status, error, proxyReturns, extrapolatedReturns, unproxiedInvestmentReturns } =
    useProxyMetricsData(investmentId, investmentLastUpdated, proxySetId);

  if (error) {
    return <p>{error}</p>;
  }

  if (status === 'loading' || !layeredProxyMetrics) {
    return <Spinner size={LoadingSize.medium} />;
  }

  return (
    <LayeredProxyInfoContent
      layeredProxyMetrics={layeredProxyMetrics}
      proxyReturns={proxyReturns}
      extrapolatedReturns={extrapolatedReturns}
      unproxiedInvestmentReturns={unproxiedInvestmentReturns}
      schemes={Schemes.Proxy}
      investment={investment}
    />
  );
};

const Container = styled.div`
  color: ${GetColor.White};
  width: 300px;
  padding: 15px 20px 0 20px;
`;

const Stats = styled.div`
  display: flex;
  justify-content: space-between;
`;
const Stat = styled.div`
  display: flex;
  flex-direction: column;
  &:first-child {
    margin-right: 20px;
  }
`;

const Title = styled.div`
  font-size: 10px;
  font-weight: bold;
`;

const Value = styled.div`
  font-size: 14px;
  padding: 3px 0 4px;
`;

const HighlightedValue = styled(Value)`
  color: ${GetScheme.Proxy.proxyLine};
`;

const ChartContainer = styled.div`
  margin-top: 14px;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  border-top: 1px solid ${GetColor.GreyScale.Grey90};
  padding-top: 5px;
  margin-top: 10px;
  font-size: 1rem;
  font-weight: normal;
  color: ${GetColor.GreyScale.Grey40};
  .proxy-info-modal-legend {
    flex-wrap: nowrap;
  }
  .legend-entry-indicator-wrapper {
    width: 11px;
    margin-right: 4px;
  }
`;
